import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Phone } from "lucide-react";
import { adminUrl } from "@/lib/adminPath";
import LOGO from "@/assets/NIRMAL-HOSPITALS-LOGO-Final.png";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/plans", label: "Plans" },
  { to: "/doctors", label: "Doctors" },
  { to: "/corporate", label: "Corporate" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return createPortal((
    <header className={`fixed inset-x-0 top-0 z-50 glass-header border-b ${scrolled ? "scrolled" : ""}`} style={{ borderColor: scrolled ? "#E1E8F0" : "transparent" }} data-testid="site-header">
      {/* Top utility bar — premium navy strip (desktop only) */}
      <div className="hidden md:block bg-gradient-to-r from-[#0F2540] via-[#1E4F87] to-[#0F2540] text-white text-xs">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href="tel:+916357033301" className="flex items-center gap-1.5 hover:text-[#F5C6BE] transition-colors font-semibold tracking-wide"><Phone className="h-3.5 w-3.5" /> +91 63570 33301</a>
            <span className="opacity-70 tracking-wide">Privilege Card Helpline · 24×7</span>
          </div>
          <div className="opacity-90 tracking-wide">Adajan, L. P. Savani · Surat — Gujarat</div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 h-[68px] md:h-20 flex items-center justify-between">
        {/* Logo lockup — no hover scale (was causing sticky-jump on some browsers) */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO} alt="Nirmal Multi Speciality Hospital" className="h-11 md:h-14 w-auto" draggable="false" />
          <div className="hidden md:flex flex-col leading-tight border-l border-[#E1E8F0] pl-3">
            <span className="font-display text-[12px] uppercase tracking-[0.22em] text-[#A53A2A] font-bold">Privilege Card</span>
            <span className="text-[11px] text-[#5C6B7A] tracking-wide italic">Care par excellence</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link group relative px-4 py-2 rounded-full text-[15px] font-semibold tracking-tight transition-colors duration-300 ${isActive ? "text-[#1E4F87] active" : "text-[#0F2540] hover:text-[#1E4F87]"}`
              }
            >
              <span className="relative inline-flex items-center">
                {n.label}
                <span className="nav-underline absolute -bottom-1 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#1E4F87] transition-all duration-300" />
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <button
                onClick={() => navigate(user.role === "admin" ? adminUrl() : "/dashboard")}
                className="px-4 py-2 rounded-full text-[14px] font-semibold text-[#0F2540] border border-[#E1E8F0] hover:bg-[#F4F7FB] transition-all"
                data-testid="header-dashboard-btn"
              >
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </button>
              <button onClick={async () => { await logout(); navigate("/"); }} className="px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#7A2A1E] hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(165,58,42,0.25)] hover:shadow-[0_12px_30px_rgba(165,58,42,0.35)] transition-all" data-testid="header-logout-btn">
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 rounded-full text-[14px] font-semibold text-[#0F2540] border border-[#E1E8F0] hover:bg-[#F4F7FB] transition-all" data-testid="header-login-btn">Log in</Link>
              <Link to="/join" className="px-5 py-2.5 rounded-full text-[14px] font-semibold text-white bg-gradient-to-r from-[#1E4F87] to-[#0F2540] hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(30,79,135,0.25)] hover:shadow-[0_12px_30px_rgba(30,79,135,0.35)] transition-all" data-testid="header-join-btn">Join NPC</Link>
            </>
          )}
        </div>

        <button className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-[#E1E8F0] hover:bg-[#F4F7FB] transition-colors" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E1E8F0] bg-white shadow-[0_20px_40px_-12px_rgba(15,37,64,0.18)]">
          <div className="px-5 py-4 flex flex-col gap-1 max-h-[calc(100vh-68px)] overflow-y-auto">
            {NAV.map((n, i) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="mobile-nav-item px-3 py-3 rounded-xl text-[16px] font-semibold text-[#0F2540] hover:bg-[#F4F7FB] transition-colors flex items-center justify-between" style={{ animationDelay: `${i * 40}ms` }} data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                <span>{n.label}</span>
                <span className="text-[#1E4F87] opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0">→</span>
              </Link>
            ))}
            <div className="h-px bg-[#E1E8F0] my-2" />
            {user ? (
              <>
                <Link to={user.role === "admin" ? adminUrl() : "/dashboard"} onClick={() => setOpen(false)} className="px-4 py-3 rounded-full text-center text-sm font-semibold text-[#0F2540] border border-[#E1E8F0]">Dashboard</Link>
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="px-4 py-3 rounded-full text-center text-sm font-semibold text-white bg-gradient-to-r from-[#A53A2A] to-[#7A2A1E]">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-3 rounded-full text-center text-sm font-semibold text-[#0F2540] border border-[#E1E8F0]">Log in</Link>
                <Link to="/join" onClick={() => setOpen(false)} className="px-4 py-3 rounded-full text-center text-sm font-semibold text-white bg-gradient-to-r from-[#1E4F87] to-[#0F2540]">Join NPC</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  ), document.body);
}
